<script lang="ts" module>
  export const puzzleSummaryModal = new ModalQueryState('summary');
</script>

<script lang="ts">
  import { innerWidth } from 'svelte/reactivity/window';
  import Play from '~icons/mdi/play';
  import Close from '~icons/mdi/close';

  import Modal from '../components/Modal.svelte';
  import ActionButton from '../components/ActionButton.svelte';
  import { prettyDate } from '~/utils/dates';
  import Board from '~/board/Board.svelte';
  import { slide } from 'svelte/transition';
  import Spinner from '~/components/Spinner.svelte';
  import FinalStars from '~/gameOver/FinalStars.svelte';
  import GameContext from '~/store/GameContext.svelte';
  import GameSummary from '~/gameOver/GameSummary.svelte';
  import { ModalQueryState } from '~/lib/nav.svelte';
  import { dims } from '~/utils/dimensions.svelte';
  import {
    puzzleQuery,
    getDailyMode,
    stringifyPuzzleKey,
  } from '~/lib/queries.svelte';
  import { navigating, page } from '$app/state';
  import type { GameMode } from '~/store/game.svelte';
  import { DailyGame } from '~/store/daily.svelte'
  import DimensionsContext from '~/utils/DimensionsContext.svelte';
  import type { DailyPuzzle } from '~/types';

  const puzzleKey = $derived(
    stringifyPuzzleKey({
      mode: getDailyMode(page.params.mode as GameMode),
      date:
        puzzleSummaryModal.value === 't' || !puzzleSummaryModal.value
          ? 'latest'
          : puzzleSummaryModal.value,
      key: page.url.searchParams.get('puzzle'),
    }),
  );

  const puzzle = $derived(puzzleQuery<DailyPuzzle>(puzzleKey));
  let clientWidth: number = $state(0);
  const size = $derived.by(() => {
    if (clientWidth && (innerWidth.current ?? 0) > 720) {
      return {
        width: clientWidth - 2 * dims.pad,
        height: clientWidth - 2 * dims.pad,
      };
    } else {
      return { width: clientWidth, height: clientWidth };
    }
  });

  const handleClose = () => {
    puzzleSummaryModal.pop();
  };
</script>

<Modal
  id="puzzle-summary"
  open={puzzleSummaryModal.open && !navigating.from}
  onClose={handleClose}
  index={3}
>
  {#snippet title()}
    <div class="title-container">
      <span class="date">
        {#if puzzleSummaryModal.value && puzzleSummaryModal.value !== 't'}
          {prettyDate(puzzleSummaryModal.value ?? '')}
        {:else if puzzle.data?.key}
          {puzzle.data.key}
        {/if}
      </span>
      <h1 class="title">
        {#if puzzle.data?.stars}
          <FinalStars stars={puzzle.data.stars} bonus={puzzle.data.fullClear} />
        {:else if puzzle.isFetched}
          Unattempted
        {/if}
      </h1>
    </div>
  {/snippet}
  {#snippet content()}
    <div class="content" bind:clientWidth>
      {#if puzzle.isFetching}
        <div class="loading" style:height={clientWidth} transition:slide>
          <Spinner />
        </div>
      {:else if puzzle.isSuccess && size.width}
        <div class="board-container" transition:slide>
          <GameContext game={new DailyGame(puzzle.data)}>
            <div style="height: fit-content">
              <DimensionsContext {...size}>
                <Board disabled hideStar />
              </DimensionsContext>
            </div>
            {#if puzzle.data.words}
              <GameSummary words={puzzle.data.words} hideStats />
            {/if}
          </GameContext>
        </div>
      {/if}
    </div>
  {/snippet}
  {#snippet controls()}
    <div class="controls">
      <ActionButton onClick={handleClose}>
        <Close size="1em" />
        Close
      </ActionButton>
      <ActionButton
        replaceState
        href="{page.url.pathname}?date={puzzle.data?.date}"
        outlink={false}
      >
        <Play size="1em" />
        Play Puzzle
      </ActionButton>
    </div>
  {/snippet}
</Modal>

<style lang="scss">
  @use '~/theme/include-media' as *;
  .title-container {
    font-size: 1rem;
    margin: -0.5rem;
  }
  .board-container {
    :global(*) {
      pointer-events: none;
    }
  }
  .title {
    margin: 0;
  }
  .date {
    font-size: 1rem;
    font-weight: 500;
    opacity: 60%;
    transform: translateX(0.5em);
  }
  .loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .content {
    width: 100%;
    text-align: center;
    :global(.tile-container) {
      z-index: unset;
    }
  }
  .controls {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: center;
    gap: 1em;
  }
</style>
